import { Button, Card, Descriptions, Message, Popconfirm, Space, Table, Tag } from "@arco-design/web-react";
import { POAPI, ItemReceiptAPI, BillAPI, VendorAPI } from "@/apis/purchasing";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Currency, Number } from "@/components/Numeric";
import { FileUtil } from "@/utils";
import { POStatusDef, POReceiptStatusDef, POStatus, BillStatusDef } from "@/enums/purchasing";
import { TermsDef } from "@/enums/common";
import { EnumLabel } from "@/components/Enum";
import Address from "@/components/Address";
import SendEmailButton from "@/components/SendEmailButton";
import Reference from "@/components/Reference";

export const POInfo = () => {
    const params = useParams();
    const poId = params.poId;
    const navigate = useNavigate();
    const [info, setInfo] = useState({});
    const [receipts, setReceipts] = useState([]);
    const [receiptsTotal, setReceiptsTotal] = useState(0);
    const [receiptsPage, setReceiptsPage] = useState(1);
    const [bills, setBills] = useState([]);
    const [billsTotal, setBillsTotal] = useState(0);
    const [billsPage, setBillsPage] = useState(1);
    const [printLoading, setPrintLoading] = useState(false);
    const [statusLoading, setStatusLoading] = useState(false);

    const getInfo = useCallback(async () => {
        const values = await POAPI.get(poId);
        setInfo(values);
    }, [poId]);

    const getReceipts = useCallback(async () => {
        const values = await ItemReceiptAPI.query(receiptsPage, { poId: poId });
        setReceipts(values.records);
        setReceiptsTotal(values.total);

    }, [receiptsPage, poId]);

    const getBills = useCallback(async () => {
        const values = await BillAPI.query({ page: billsPage, poId: poId });
        setBills(values.records);
        setBillsTotal(values.total);
    }, [billsPage, poId]);

    const updateStatus = async (status) => {
        setStatusLoading(true);
        try {
            await POAPI.updateStatus(info.id, status);
            Message.success("Status updated successfully");
            await getInfo();
        } catch (error) {
            Message.error(error.message);
        } finally {
            setStatusLoading(false);
        }
    }

    useEffect(() => {
        getInfo();
    }, [getInfo])

    useEffect(() => {
        getReceipts();
    }, [getReceipts])

    useEffect(() => {
        getBills();
    }, [getBills])

    return <Card title={
        <Space>
            <span>{info.poNo}</span>
            <Tag><EnumLabel enumObj={POStatusDef} value={info.status} /></Tag>
        </Space>
    } extra={
        <Space>
            {[POStatus.Created, POStatus.Issued, POStatus.Partial].includes(info.status) &&
                <Button type="primary" onClick={() => navigate(`/workspace/purchasing/po/${info.id}/edit`)}>Edit</Button>
            }
            {(info.status === POStatus.Created) &&
                <Button type="primary" onClick={() => updateStatus(POStatus.Issued)} loading={statusLoading}>Issue</Button>
            }
            {(info.status === POStatus.Issued) &&
                <Button type="primary" onClick={() => updateStatus(POStatus.Created)} loading={statusLoading}>Unissue</Button>
            }
            {[POStatus.Created, POStatus.Issued, POStatus.Partial].includes(info.status) &&
                <Button type="primary" onClick={() => navigate("/workspace/purchasing/receipt/po/" + info.id)}>Receive PO</Button>
            }
            {(info.status === POStatus.Partial) &&
                <Button type="primary" onClick={() => updateStatus(POStatus.ShortClosed)} loading={statusLoading}>Short Close</Button>
            }
            {(info.status === POStatus.Created || info.status === POStatus.Issued) &&
                <Popconfirm title="Are you sure you want to void purchasing order?"
                    onOk={() => updateStatus(POStatus.Voided)}>
                    <Button type="primary" status="danger" loading={statusLoading}>Void</Button>
                </Popconfirm>
            }
            {info.status !== POStatus.Voided &&
                <Button type="primary" onClick={async () => {
                    setPrintLoading(true);
                    try {
                        const res = await POAPI.print(info.id);
                        FileUtil.download(res.data, `${info.poNo}.pdf`);
                    } catch (error) {
                        Message.error(error.message);
                    } finally {
                        setPrintLoading(false);
                    }
                }} loading={printLoading}>Print</Button>
            }
            {info.status !== POStatus.Voided &&
                <SendEmailButton getEmailContent={async () => {
                    return await POAPI.getEmail(info.id);
                }} getToList={async () => {
                    const vendor = await VendorAPI.get(info.vendorId);
                    return vendor.contacts;
                }} sendEmail={async (val) => {
                    await POAPI.sendEmail(info.id, val);
                }} />
            }
            <Button onClick={() => navigate("/workspace/purchasing/po")}>Back</Button>
        </Space >
    }>

        <Descriptions colon=":" column={4}
            data={[{
                label: "Vendor",
                value: info.vendorName
            }, {
                label: "Drop Ship",
                value: info.dropShipName
            }, {
                label: "Ship to",
                value: <Address value={{
                    name: info.shipToContactName,
                    address: info.shipToAddress,
                    city: info.shipToCity,
                    state: info.shipToState,
                    zip: info.shipToZip,
                }} inline />
            }, {
                label: "PO Date",
                value: info.poDate
            }, {
                label: "Due Date",
                value: info.dueDate
            }, {
                label: "ETA",
                value: info.eta
            }, {
                label: "Term",
                value: <EnumLabel enumObj={TermsDef} value={info.term} />
            }, {
                label: "Total Amount",
                value: <Currency value={info.totalAmount} />
            }, {
                label: "Reference#",
                value: <Reference value={info.reference} />,
                span: 2
            }, {
                label: "Memo",
                value: info.memo,
                span: 2
            }]} />

        <Table rowKey="id" columns={[
            { title: "Item Number", dataIndex: "itemNo" },
            { title: "Item Name", dataIndex: "itemName" },
            { title: "Description", dataIndex: "description" },
            {
                title: "Total Qty",
                render: (col, record) => <Number value={record.quantity} />
            },
            {
                title: "Qty Received",
                render: (col, record) => <Number value={record.received} />
            },
            {
                title: "Open",
                render: (col, record) => <Number value={Math.max(record.quantity - record.received, 0)} />
            },
            { title: "U/M", dataIndex: "uom" },
            { title: "Rate", render: (col, record) => <Currency value={record.rate} /> },
            { title: "Amount", render: (col, record) => <Currency value={record.amount} /> },
        ]}
            data={info.items}

        />

        <Table rowKey="id" style={{ marginTop: 15 }} columns={[
            {
                title: "Item Receipt#",
                render: (col, record, index) =>
                    <Link to={"/workspace/purchasing/receipt/" + record.id}>{record.receiptNo}</Link>
            },
            { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={POReceiptStatusDef} value={record.status} /> },
            { title: "Vendor", dataIndex: "vendorName" },
            { title: "Receive Date", dataIndex: "receiveDate" },

        ]} data={receipts}
            pagination={{ total: receiptsTotal, current: receiptsPage, pageSize: 20 }}
            onChange={(pagination) => { setReceiptsPage(pagination.current); }}
        />

        <Table rowKey="id" style={{ marginTop: 15 }} columns={[
            {
                title: "Bill#", render: (col, record, index) => {
                    return (
                        <Link to={"/workspace/purchasing/bills/" + record.id}>{record.billNo}</Link>
                    )
                }
            },
            { title: "Bill Date", dataIndex: "billDate" },
            { title: "Due Date", dataIndex: "dueDate" },
            { title: "Vendor", dataIndex: "vendorName" },
            { title: "Total Amount", render: (col, record) => <Currency value={record.totalAmount} /> },
            { title: "Invoice#", dataIndex: "invoiceNo" },
            { title: "Status", dataIndex: "status", render: (col, record) => <EnumLabel enumObj={BillStatusDef} value={record.status} /> },
            { title: "Paid Date", dataIndex: "processDate" },
            { title: "Clear Date", dataIndex: "clearDate" },

        ]} data={bills}
            pagination={{ total: billsTotal, current: billsPage, pageSize: 20 }}
            onChange={(pagination) => { setBillsPage(pagination.current); }}
        />
    </Card >
}
